import { BearingsTableStoreProvider } from '@hub/bearing-hub/BearingHubPage/BearingsTable/useBearingsTableStore';
import type { ProductDetailPageProps } from '@hub/bearing-hub/ProductDetails/ProductDetails';
import { ProductDetails } from '@hub/bearing-hub/ProductDetails/ProductDetails';
import type { Intersect } from '@hub/bearing-hub/common/types';
import { getPublicShopSession } from '@hub/libs/catalog-service';
import { getCmsContents } from '@hub/libs/cms.helper';
import {
  getLocale,
  WIDEN_DEFAULT_ONLINE_TOOLS_TEASERS_IMAGE_HEIGHT,
  WIDEN_DEFAULT_ONLINE_TOOLS_TEASERS_IMAGE_WIDTH,
} from '@hub/libs/constants';
import { getSingleProductAttributes, getTeaser } from '@hub/libs/graphql.helper.server';
import { getIbhLanguageAlternates, getServerSideTranslations } from '@hub/libs/i18n.helper';
import type { ImageLink } from '@hub/models/api/page-content.types';
import type { LanguageAlternate, ProductDetailsParams } from '@hub/models/data';
import type { GetServerSideProps } from 'next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import type { Session } from '@hub/hooks/states/useSessionStore';
import { BEARINGHUB_NODEID_OVERWRITES, DEFAULT_QUANTITY } from '@hub/bearing-hub/common/constants';
import { getSessionPayload } from '@hub/common/Session/session.utils';
import { getSuitableProducts } from '@hub/bearing-hub/ProductDetails/SuitableProducts/getSuitableProducts';
import { getUniqueIbhAssociationProducts } from '@hub/bearing-hub/ProductDetails/SuitableProducts/product-associations.helper.server';
import type { AssociationIbhProductFragmentEnhanced, ProductFragmentEnhanced } from '@hub/models/enhanced-fragments';
import * as Sentry from '@sentry/nextjs';
import { appPrefix, getArticleDataFromContext } from '@hub/bearing-hub/common/link.factory';
import { getIbhProduct, replaceLinks } from '@hub/libs/bearing-hub.helper.server';
import { getUniqueAssociatedProductIds } from '@hub/bearing-hub/ProductDetails/SuitableProducts/product-associations.helper';
import { getInitialHeaderData } from '@hub/page-helpers/getInitialHeaderData';
import { getTechnicalDataCategories } from '@hub/bearing-hub/ProductDetails/ProductTabs/technical-data.factory';
import { getTechnicalDataWithAttributeNames } from '@hub/bearing-hub/ProductDetails/AkeneoProductData/product-technical-data';
import { getAttributeDefinitions } from '@hub/bearing-hub/ProductDetails/AkeneoProductData/getAttributeDefinitions';
import type {
  ProductAttribute,
  ProductAttributeUnion,
} from '@hub/bearing-hub/ProductDetails/AkeneoProductData/attribute.helper';
import { Article } from '@hub/bearing-hub/ProductDetails/ArticleData/article';
import { getArticleNumber } from '@hub/bearing-hub/ProductDetails/ArticleData/articleNumber';
import { getCustomArticleDimension } from '@hub/bearing-hub/ProductDetails/ArticleData/articleDimensions';
import type { IbhPartsResponseDto } from '@igus/iglidur-bearing-hub-api/src/data-contracts';
import { IbhProductionMethods } from '@igus/iglidur-bearing-hub-api/src/data-contracts';
import { fetchSimilarParts } from '@hub/bearing-hub/BearingHubPage/parts/fetchSimilarParts';
import { getFeatureFlagValue } from '@hub/libs/abby/abby';
import { LifetimeParametersStoreProvider } from '@hub/bearing-hub/libs/BearingsLifetime/useIbhLifetimeParametersValuesStore';

export const getServerSideProps: GetServerSideProps<ProductDetailPageProps, ProductDetailsParams> = async context => {
  if (!context.params || !context.params?.shape) {
    Sentry.captureException(new Error(`Missing shape parameter like .../iglid(ur|e)-ibh/[sleeve-bearings]`));
    return { notFound: true };
  }

  if (!context.params.categoryName) {
    Sentry.captureException(
      new Error(`Missing articleNumber parameter like .../iglid(ur|e)-ibh/sleeve-bearings/[GTM]`)
    );
    return { notFound: true };
  }

  const shapePath = context.params.shape;
  const locale = getLocale(context);
  const { materialName, unitSystem, isCustom, selectedShapeDescriptor, productionMethod } =
    getArticleDataFromContext(context);
  const { params } = context;
  const { categoryName } = params;
  const categoryNameIsValid = typeof categoryName === 'string' && categoryName.split('-')[0] === appPrefix;
  const quantity = DEFAULT_QUANTITY;
  const useIcn = getFeatureFlagValue('ibh_useIcn');

  if (!categoryNameIsValid) {
    Sentry.captureException(new Error(`Category name "${categoryName}" is invalid`));
    return { notFound: true };
  }

  if (!selectedShapeDescriptor) {
    Sentry.captureException(new Error(`Shape not found with parameter shapePath = "${shapePath}"`));
    return { notFound: true };
  }
  const articleNumber = await getArticleNumber(
    selectedShapeDescriptor.shapeKey,
    materialName,
    unitSystem,
    useIcn && productionMethod === IbhProductionMethods.SEMIFINISHED,
    context.query?.artnr as string
  );

  if (!articleNumber) {
    Sentry.captureException(
      new Error(`No default article for selected category: categoryName = "${context.params.categoryName}"`)
    );
    return { notFound: true };
  }

  const session: Session = await getPublicShopSession(getSessionPayload(locale));

  if (!session) {
    Sentry.captureException(new Error(`Session could not be retrieved`));
    return { notFound: true };
  }

  const customArticleDimensions = getCustomArticleDimension(context.query?.artnr as string);
  const article = Article({
    articleNumber,
    isCustom,
    quantity,
    shapeKey: selectedShapeDescriptor.shapeKey,
    unitSystem,
    dimensions: customArticleDimensions,
    session,
    useIcn: useIcn && productionMethod === IbhProductionMethods.SEMIFINISHED,
  });

  const articleData = await article.fetchArticleData();

  if (!articleData) {
    Sentry.captureException(new Error(`Article data could not be retrieved: articleNumber = "${articleNumber}"`));
    return { notFound: true };
  }

  const dimensions = isCustom ? customArticleDimensions : articleData.dimensions;

  if (selectedShapeDescriptor.shapeKey !== articleData.shape) {
    Sentry.captureException(
      new Error(`Article not matching with given shape: articleNumber = "${articleNumber}", shape = "${shapePath}"`)
    );
    return {
      notFound: true,
    };
  }

  if (materialName !== articleData.material.id) {
    Sentry.captureException(
      new Error(
        `Article not matching with given material: articleNumber = "${articleNumber}", material = "${materialName}"`
      )
    );
    return {
      notFound: true,
    };
  }

  const { articleId, haveCatalogNumber } = article.getArticleIdToFetchDataFromAkeneo(articleData);
  if (!articleId) {
    Sentry.captureException(
      new Error(`Article id could not be extracted from data: articleNumber = "${articleNumber}"`)
    );
    return { notFound: true };
  }

  if (unitSystem !== articleData.unitSystem) {
    Sentry.captureException(
      new Error(
        `Article not matching with given unitSystem: articleNumber = "${articleNumber}", material = "${unitSystem}"`
      )
    );
    return {
      notFound: true,
    };
  }

  const AkeneoProductData = await getIbhProduct(articleId, locale);

  if (!AkeneoProductData) {
    if (!isCustom || haveCatalogNumber) {
      Sentry.captureException(
        new Error(`Catalog article could not be loaded from akeneo: articleNumber = "${encodeURIComponent(articleId)}"`)
      );
    }

    if (!AkeneoProductData) {
      Sentry.captureException(
        new Error(`Custom article could not be loaded from akeneo: articleNumber = "${encodeURIComponent(articleId)}"`)
      );
    }
  }

  let similarParts: IbhPartsResponseDto[] = [];

  if (isCustom) {
    similarParts = await fetchSimilarParts(
      {
        sid: session.sessionId,
      },
      {
        shape: selectedShapeDescriptor.shapeKey,
        productionMethods: [articleData.productionMethod],
        unitSystem: articleData.unitSystem,
        quantity,
        materials: [articleData.material.id],
        dimensions,
      }
    );
  }
  const [{ technicalData, technicalDataAttributeNames }, productAttributes] = await Promise.all([
    getTechnicalDataWithAttributeNames(
      locale === 'de-DE' ? 'de-DE' : 'en-IN',
      false,
      AkeneoProductData?.technicalDataEntityKey
    ),
    getSingleProductAttributes(articleId, locale),
  ]);
  const attributeDefinitions = await getAttributeDefinitions(technicalDataAttributeNames, locale);
  const technicalDataCategories = getTechnicalDataCategories(
    technicalData,
    productAttributes as Record<string, ProductAttribute<Intersect<ProductAttributeUnion>>>,
    attributeDefinitions,
    articleData,
    locale,
    unitSystem,
    dimensions,
    isCustom
  );

  const nodeContactId = BEARINGHUB_NODEID_OVERWRITES.default;

  const { contact, footer } = await getCmsContents(nodeContactId, locale);

  const onlineToolsDefinitions = await getTeaser(
    'onlinetoolsteaser',
    locale,
    WIDEN_DEFAULT_ONLINE_TOOLS_TEASERS_IMAGE_WIDTH,
    WIDEN_DEFAULT_ONLINE_TOOLS_TEASERS_IMAGE_HEIGHT
  );

  footer?.subscribe?.links?.forEach(replaceLinks);
  footer?.awards?.links?.forEach((link: ImageLink) => replaceLinks(link));

  let languageAlternates: LanguageAlternate[] = [];
  if (selectedShapeDescriptor) {
    languageAlternates = getIbhLanguageAlternates(locale, {
      selectedShape: selectedShapeDescriptor,
      productKey: articleNumber,
      materialName,
      unitSystem: articleData.unitSystem,
    });
  }

  const ids = AkeneoProductData ? getUniqueAssociatedProductIds([AkeneoProductData] as ProductFragmentEnhanced[]) : [];
  const uniqueAssociationProducts = await getUniqueIbhAssociationProducts(ids, locale);
  const finalFittingSuitableProducts: Record<string, AssociationIbhProductFragmentEnhanced> = {};
  Object.keys(uniqueAssociationProducts)
    .map(key => uniqueAssociationProducts[key])
    .filter(uniqueAssociationProduct => {
      if (isCustom) {
        if (uniqueAssociationProduct.outerDiameter?.amount === dimensions.d1) {
          finalFittingSuitableProducts[uniqueAssociationProduct.id] = uniqueAssociationProduct;
        }
      } else if (
        AkeneoProductData.innerDiameter &&
        uniqueAssociationProduct.outerDiameter?.amount === AkeneoProductData.innerDiameter?.amount
      ) {
        finalFittingSuitableProducts[uniqueAssociationProduct.id] = uniqueAssociationProduct;
      }
    });

  const suitableProducts = AkeneoProductData
    ? getSuitableProducts(AkeneoProductData as ProductFragmentEnhanced, finalFittingSuitableProducts, locale, true)
    : [];

  const header = await getInitialHeaderData(locale, languageAlternates);

  const props: ProductDetailPageProps = {
    languageAlternates,
    onlineToolsDefinitions,
    selectedShape: selectedShapeDescriptor,
    header,
    footer,
    contact,
    articleNumber,
    articleData,
    akeneoProductData: AkeneoProductData,
    dimensions,
    isCustom,
    technicalDataCategories,
    useIcn,
    suitableProducts: suitableProducts.filter(
      suitableProduct => suitableProduct?.title && suitableProduct.usp && suitableProduct.image
    ),
    similarParts,
    ...(await getServerSideTranslations(serverSideTranslations, locale, [
      'bearing-hub/bearingHub',
      'layout/header',
      'toast/toast',
      'layout/contactPersonBox',
      'layout/localeSelection',
      'layout/breadcrumb',
      'layout/contactBox',
      'layout/shopInfo',
      'layout/footer',
      'layout/general',
      'product/buyBox',
      'product/deliveryInfo',
      'product/productDetails',
      'product/mobileMainMenu',
      'category/categoryPage',
      'category/productTeaser',
      'free-form-hub/freeFormHubCommon',
      'free-form-hub/materials',
      'free-form-hub/materialsDescription',
    ])),
  };

  return {
    props,
  };
};

const ProductDetailsPage = ({
  languageAlternates,
  onlineToolsDefinitions,
  header,
  footer,
  selectedShape,
  contact,
  articleNumber,
  articleData,
  akeneoProductData,
  dimensions,
  isCustom,
  technicalDataCategories,
  suitableProducts,
  similarParts,
  useIcn,
}: ProductDetailPageProps) => (
  <BearingsTableStoreProvider initialData={{ selectedShape }}>
    <LifetimeParametersStoreProvider unitSystem={articleData.unitSystem}>
      <ProductDetails
        onlineToolsDefinitions={onlineToolsDefinitions}
        header={header}
        footer={footer}
        selectedShape={selectedShape}
        contact={contact}
        articleNumber={articleNumber}
        articleData={articleData}
        akeneoProductData={akeneoProductData}
        dimensions={dimensions}
        isCustom={isCustom}
        technicalDataCategories={technicalDataCategories}
        suitableProducts={suitableProducts}
        similarParts={similarParts}
        languageAlternates={languageAlternates}
        useIcn={useIcn}
      />
    </LifetimeParametersStoreProvider>
  </BearingsTableStoreProvider>
);

export default ProductDetailsPage;
